import React from 'react';
import { faDiagramSuccessor } from '@fortawesome/free-solid-svg-icons';
import PageHeader from 'layout/PageHeader';
import { ConfirmDialog } from '../../../common';
import {
	fetchWorkflowRules,
	updateWorkflowRule,
} from '../../../common/services/workflowApi';
import { WorkflowRuleView } from '../../../common/services/workflowClient';
import {
	groupForRule,
	workflowCategories,
	workflowGroups,
	WorkflowGroup,
} from '../workflowGroups';
import WorkflowRuleRow from './WorkflowRuleRow';

const WorkflowSettings: React.FC = () => {
	const [rules, setRules] = React.useState<WorkflowRuleView[]>([]);
	const [drafts, setDrafts] = React.useState<
		Record<string, Record<string, unknown>>
	>({});
	const [openCode, setOpenCode] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [saving, setSaving] = React.useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const pendingRef = React.useRef<(() => void) | null>(null);

	const load = React.useCallback(() => {
		const token = localStorage.getItem('token');
		if (!token) return;
		fetchWorkflowRules(token)
			.then((rows) => {
				setRules(rows);
				setDrafts(
					Object.fromEntries(rows.map((rule) => [rule.code, rule.parameters]))
				);
				setError(null);
			})
			.catch(() => setError('Impossible de charger les workflows.'));
	}, []);

	React.useEffect(() => {
		load();
	}, [load]);

	const token = () => localStorage.getItem('token') ?? '';

	const isDirty = (code: string) => {
		const rule = rules.find((item) => item.code === code);
		if (!rule) return false;
		return (
			JSON.stringify(drafts[code] ?? {}) !==
			JSON.stringify(rule.parameters ?? {})
		);
	};

	const patch = async (
		rule: WorkflowRuleView,
		payload: Partial<
			Pick<WorkflowRuleView, 'enabled' | 'priority' | 'parameters'>
		>
	) => {
		setSaving(rule.code);
		try {
			const updated = await updateWorkflowRule(token(), rule.code, payload);
			setRules((current) =>
				current.map((item) => (item.code === updated.code ? updated : item))
			);
			setDrafts((current) => ({
				...current,
				[updated.code]: payload.parameters
					? updated.parameters
					: (current[updated.code] ?? updated.parameters),
			}));
			setError(null);
		} catch {
			setError('Enregistrement impossible.');
		} finally {
			setSaving(null);
		}
	};

	const discardOpenDraft = () => {
		if (!openCode) return;
		const rule = rules.find((item) => item.code === openCode);
		if (!rule) return;
		setDrafts((current) => ({ ...current, [openCode]: rule.parameters }));
	};

	const guard = (action: () => void) => {
		if (openCode && isDirty(openCode)) {
			pendingRef.current = action;
			setConfirmOpen(true);
			return;
		}
		action();
	};

	const confirmDiscard = () => {
		discardOpenDraft();
		const action = pendingRef.current;
		pendingRef.current = null;
		setConfirmOpen(false);
		action?.();
	};

	const stay = () => {
		pendingRef.current = null;
		setConfirmOpen(false);
	};

	const toggleParameters = (code: string) => {
		if (openCode === code) {
			guard(() => setOpenCode(null));
			return;
		}
		guard(() => setOpenCode(code));
	};

	const move = (
		groupRules: WorkflowRuleView[],
		index: number,
		direction: -1 | 1
	) => {
		const target = index + direction;
		if (target < 0 || target >= groupRules.length || saving !== null) return;
		const current = groupRules[index];
		const other = groupRules[target];
		void patch(current, { priority: other.priority }).then(() =>
			patch(other, { priority: current.priority })
		);
	};

	const rulesOf = (group: WorkflowGroup) =>
		rules
			.filter((rule) => group.match(rule.code))
			.sort((a, b) => a.priority - b.priority);

	const ungrouped = rules.filter((rule) => !groupForRule(rule.code));

	return (
		<div>
			<PageHeader
				icon={faDiagramSuccessor}
				title="Workflows"
				subtitle="Règles de cette instance, dans l'ordre du menu"
				className="mb-6 !max-w-full"
			/>
			<p className="mb-8 text-sm text-gray-600">
				{
					"Chaque ligne est une règle. L'ordre dans un groupe est l'ordre d'exécution. Une règle désactivée retrouve le comportement libre d'origine."
				}
			</p>
			{error && <p className="mb-4 text-sm text-red-700">{error}</p>}
			{rules.length === 0 && !error && (
				<p className="text-sm text-gray-500">Chargement des règles…</p>
			)}
			{workflowCategories.map((category) => {
				const groups = workflowGroups.filter(
					(group) => group.category === category.id && rulesOf(group).length > 0
				);
				if (groups.length === 0) return null;
				return (
					<section key={category.id} className="mb-16">
						<div className="mb-4 flex items-center">
							<h2 className="mr-2 text-xl font-medium">{category.title}</h2>
							<span
								className={`rounded-full px-2 py-0.5 text-sm font-medium ${category.badgeClass}`}
							>
								{category.short}
							</span>
						</div>
						{groups.map((group) => {
							const groupRules = rulesOf(group);
							return (
								<div key={group.id} className="mb-8">
									<div className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 shadow-sm">
										{groupRules.map((rule, index) => (
											<WorkflowRuleRow
												key={rule.code}
												rule={rule}
												icon={group.icon}
												draft={drafts[rule.code] ?? {}}
												open={openCode === rule.code}
												saving={saving === rule.code}
												canMoveUp={index > 0}
												canMoveDown={index < groupRules.length - 1}
												onToggleEnabled={(enabled) =>
													void patch(rule, { enabled })
												}
												onMove={(direction) =>
													move(groupRules, index, direction)
												}
												onToggleParameters={() => toggleParameters(rule.code)}
												onDraftChange={(key, value) =>
													setDrafts((current) => ({
														...current,
														[rule.code]: {
															...current[rule.code],
															[key]: value,
														},
													}))
												}
												onSaveParameters={() =>
													void patch(rule, { parameters: drafts[rule.code] })
												}
											/>
										))}
									</div>
								</div>
							);
						})}
					</section>
				);
			})}
			{ungrouped.length > 0 && (
				<section className="mb-16">
					<h2 className="mb-4 text-xl font-medium">Autres</h2>
					<div className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 shadow-sm">
						{ungrouped.map((rule) => (
							<WorkflowRuleRow
								key={rule.code}
								rule={rule}
								icon={faDiagramSuccessor}
								draft={drafts[rule.code] ?? {}}
								open={openCode === rule.code}
								saving={saving === rule.code}
								canMoveUp={false}
								canMoveDown={false}
								onToggleEnabled={(enabled) => void patch(rule, { enabled })}
								onMove={() => undefined}
								onToggleParameters={() => toggleParameters(rule.code)}
								onDraftChange={(key, value) =>
									setDrafts((current) => ({
										...current,
										[rule.code]: { ...current[rule.code], [key]: value },
									}))
								}
								onSaveParameters={() =>
									void patch(rule, { parameters: drafts[rule.code] })
								}
							/>
						))}
					</div>
				</section>
			)}
			<ConfirmDialog
				open={confirmOpen}
				title="Modifications non enregistrées"
				description="Des paramètres n'ont pas été enregistrés. Les abandonner ?"
				confirmColor="red"
				onConfirm={confirmDiscard}
				onCancel={stay}
			/>
		</div>
	);
};

export default WorkflowSettings;
